import * as zod from 'zod';

// eslint-disable-next-line no-restricted-imports
import { CBVoltage, ModelType } from '@sb/robot';
import { six } from '@sb/utilities';

/**
 * A set of arm joint accelerations in rad/s^2,
 * in order from joint 0 to joint 5.
 */
export const ArmJointAccelerations = zod.tuple([
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
]);

export type ArmJointAccelerations = zod.infer<typeof ArmJointAccelerations>;

// radians per second^2
export const ABSOLUTE_MAX_JOINT_ACCELERATIONS: ArmJointAccelerations = six(5);
export const ABSOLUTE_MAX_JOINT_ACCELERATIONS_HIGH: ArmJointAccelerations =
  six(12);

export const COLLABORATIVE_MAX_JOINT_ACCELERATIONS: ArmJointAccelerations =
  six(1.0);

export const DEFAULT_MAX_JOINT_ACCELERATIONS =
  COLLABORATIVE_MAX_JOINT_ACCELERATIONS;

// Matches the 0kg (unloaded) row of THOR_PAYLOAD_ACCELERATION_TABLE — the
// absolute hardware ceiling. Payload-aware clamping in
// `payloadMassToMaxAccelerationsForTable` further restricts this at runtime.
export const THOR_MAX_JOINT_ACCELERATIONS: ArmJointAccelerations = [
  10.0, 6.0, 10.0, 10.0, 10.0, 10.0,
];

// Thor payload-to-acceleration lookup table. Sorted descending by mass.
// `payloadMassToMaxAccelerationsForTable` linearly interpolates between rows.
export const THOR_PAYLOAD_ACCELERATION_TABLE: {
  mass: number;
  accelerations: ArmJointAccelerations;
}[] = [
  { mass: 30, accelerations: [10.0, 1.0, 5.0, 10.0, 10.0, 10.0] },
  { mass: 25, accelerations: [10.0, 1.7, 6.1, 10.0, 10.0, 10.0] },
  { mass: 20, accelerations: [10.0, 2.4, 7.3, 10.0, 10.0, 10.0] },
  { mass: 16, accelerations: [10.0, 3.2, 8.5, 10.0, 10.0, 10.0] },
  { mass: 9, accelerations: [10.0, 4.4, 9.5, 10.0, 10.0, 10.0] },
  { mass: 4.5, accelerations: [10.0, 5.4, 9.9, 10.0, 10.0, 10.0] },
  { mass: 0, accelerations: [10.0, 6.0, 10.0, 10.0, 10.0, 10.0] },
];

export const THOR_24V42V_MAX_JOINT_ACCELERATIONS: ArmJointAccelerations = [
  5.0, 1.0, 6.0, 6.0, 6.0, 6.0,
];

// Pre-firmware-validation 24V/42V table (restored from parent of #17836).
// Same row format as THOR_PAYLOAD_ACCELERATION_TABLE; the runtime
// interpolator (`payloadMassToMaxAccelerationsForTable`) supports both.
export const THOR_24V42V_PAYLOAD_ACCELERATION_TABLE: {
  mass: number;
  accelerations: ArmJointAccelerations;
}[] = [
  { mass: 30, accelerations: [5.0, 0.3, 0.75, 6.0, 6.0, 6.0] },
  { mass: 27, accelerations: [5.0, 0.35, 0.8, 6.0, 6.0, 6.0] },
  { mass: 23, accelerations: [5.0, 0.35, 1.8, 6.0, 6.0, 6.0] },
  { mass: 16, accelerations: [5.0, 0.45, 4.0, 6.0, 6.0, 6.0] },
  { mass: 11, accelerations: [5.0, 0.55, 5.0, 6.0, 6.0, 6.0] },
  { mass: 5, accelerations: [5.0, 0.7, 6.0, 6.0, 6.0, 6.0] },
  { mass: 0, accelerations: [5.0, 1.0, 6.0, 6.0, 6.0, 6.0] },
];

export function getThorPayloadAccelTableForCBVoltage(
  voltage: CBVoltage | undefined,
): { mass: number; accelerations: ArmJointAccelerations }[] {
  return voltage === CBVoltage.enum.V48
    ? THOR_PAYLOAD_ACCELERATION_TABLE
    : THOR_24V42V_PAYLOAD_ACCELERATION_TABLE;
}

export function getThorMaxAccelForCBVoltage(
  voltage: CBVoltage | undefined,
): ArmJointAccelerations {
  return voltage === CBVoltage.enum.V48
    ? THOR_MAX_JOINT_ACCELERATIONS
    : THOR_24V42V_MAX_JOINT_ACCELERATIONS;
}

export const ABSOLUTE_MAX_ACCELERATION_COLLISION_THRESHOLDS = six(500);

/**
 * The default change in acceleration thresholds for collision detection
 * (used for RO2/Core arms). Collision threshold is the change in acceleration
 * over a short period of time at which the joint is considered to be in a
 * collision state. Values are monitored and reported by IMU in firmware.
 * See THOR_DEFAULT_ACCELERATION_COLLISION_THRESHOLDS for Thor-specific values.
 */
export const DEFAULT_ACCELERATION_COLLISION_THRESHOLDS = six(15);

/**
 * Thor arms move at higher accelerations, so the EE/wrist IMU needs a higher
 * threshold to avoid false-positive collision detections during normal motion.
 * J0-J4 stay at 15; J5 (and the EE board which mirrors J5) is raised to 40.
 */
export const THOR_DEFAULT_ACCELERATION_COLLISION_THRESHOLDS: ArmJointAccelerations =
  [15, 15, 15, 15, 15, 40];

export const DEFAULT_JOINT_STOPPING_ACCELERATIONS: ArmJointAccelerations = [
  1.55, 1.2, 1.5, 2.9, 2.9, 2.9,
];

export const THOR_DEFAULT_JOINT_STOPPING_ACCELERATIONS: ArmJointAccelerations =
  [1.55, 1.0, 1.5, 2.9, 2.9, 2.9];

export const SPARK_DEFAULT_JOINT_STOPPING_ACCELERATIONS: ArmJointAccelerations =
  [1.55, 1.2, 1.5, 2.9, 2.9, 2.9];

// Per-joint minimum stopping deceleration of zero, i.e. "no hardware-enforced
// stopping floor". Used for models (currently all non-Thor) where the user is
// free to dial stopping accelerations down to their configured/default value.
export const NO_STOPPING_ACCELERATION_FLOOR: ArmJointAccelerations = six(0);

export function getMaxJointAccelerationsForModel(
  modelType: ModelType | undefined,
): ArmJointAccelerations {
  switch (modelType) {
    case ModelType.enum.Thor:
      return THOR_MAX_JOINT_ACCELERATIONS;
    default:
      return ABSOLUTE_MAX_JOINT_ACCELERATIONS;
  }
}
